import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaFileContract, FaGavel, FaBalanceScale, FaCopyright, 
  FaExclamationTriangle, FaHandshake, FaShieldAlt, FaBan 
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const [ref] = useInView({ triggerOnce: true, threshold: 0.1 });

  const sections = [
    {
      icon: FaHandshake,
      title: 'Acceptance of Terms',
      content: [
        {
          text: 'By accessing and using the Sronzer website ("Website"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you must not access or use our Website.'
        },
        {
          text: 'These Terms constitute a legally binding agreement between you and Sronzer ("Company," "we," "us," or "our"). We reserve the right to modify these Terms at any time, and such modifications shall be effective immediately upon posting.'
        }
      ]
    },
    {
      icon: FaShieldAlt,
      title: 'Use of Website',
      content: [
        {
          subtitle: 'Eligibility',
          text: 'You must be at least 18 years of age to use our Website. By using the Website, you represent and warrant that you meet all eligibility requirements.'
        },
        {
          subtitle: 'Lawful Use',
          text: 'You agree to use our Website only for lawful purposes and in accordance with these Terms. You agree not to use the Website in any way that violates applicable laws, regulations, or export control restrictions.'
        },
        {
          subtitle: 'Account Security',
          text: 'If you create an account, you are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.'
        }
      ]
    },
    {
      icon: FaCopyright,
      title: 'Intellectual Property Rights',
      content: [
        {
          subtitle: 'Ownership',
          text: 'All content, features, and functionality on this Website, including text, graphics, logos, images, software, and design elements, are the exclusive property of Sronzer and are protected by Indian and international copyright, trademark, and intellectual property laws.'
        },
        {
          subtitle: 'Restrictions',
          text: 'You may not reproduce, distribute, modify, create derivative works of, publicly display, or exploit any content from our Website without prior written consent from Sronzer.'
        },
        {
          subtitle: 'Trademarks',
          text: 'The Sronzer name, logo, and all related names, logos, product names, and service names are trademarks of the Company. You must not use such marks without prior written permission.'
        }
      ]
    },
    {
      icon: FaBan,
      title: 'Prohibited Activities',
      content: [
        {
          text: 'You agree not to:'
        },
        {
          list: [
            'Use the Website for any illegal or unauthorized purpose',
            'Attempt to gain unauthorized access to any part of the Website or its systems',
            'Interfere with the proper working of the Website or any activities conducted on it',
            'Upload or transmit viruses, malware, or any malicious code',
            'Use any automated means (bots, scrapers) to access or collect data from the Website',
            'Misrepresent your identity or affiliation with any person or organization',
            'Violate export control laws related to defense technologies',
            'Use the Website to transmit sensitive military or classified information without authorization'
          ]
        }
      ]
    },
    {
      icon: FaExclamationTriangle,
      title: 'Disclaimers & Limitation of Liability',
      content: [
        {
          subtitle: 'No Warranty',
          text: 'The Website and all content are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied. We do not warrant that the Website will be uninterrupted, secure, or error-free.'
        },
        {
          subtitle: 'Defense Information',
          text: 'Information about defense products and technologies on this Website is for general informational purposes only. Technical specifications may change without notice. Actual product capabilities may vary based on configuration and operational conditions.'
        },
        {
          subtitle: 'Limitation of Liability',
          text: 'To the fullest extent permitted by law, Sronzer shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Website or any products or services described herein.'
        }
      ]
    },
    {
      icon: FaBalanceScale,
      title: 'Indemnification',
      content: [
        {
          text: 'You agree to indemnify, defend, and hold harmless Sronzer, its officers, directors, employees, agents, and affiliates from any claims, liabilities, damages, losses, costs, or expenses arising from:'
        },
        {
          list: [
            'Your violation of these Terms',
            'Your use of the Website',
            'Your violation of any third-party rights',
            'Any unauthorized use of your account'
          ]
        }
      ]
    },
    {
      icon: FaGavel,
      title: 'Governing Law & Jurisdiction',
      content: [
        {
          text: 'These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of the Website shall be subject to the exclusive jurisdiction of the courts in Bhopal, Madhya Pradesh.'
        },
        {
          text: 'For defense-related matters, additional regulations under the Defence Procurement Procedure (DPP) and other applicable Indian defense regulations may apply.'
        }
      ]
    },
    {
      icon: FaFileContract,
      title: 'Termination',
      content: [
        {
          text: 'We reserve the right to terminate or suspend your access to the Website immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the Website will cease immediately.'
        },
        {
          text: 'All provisions of these Terms that by their nature should survive termination shall survive, including intellectual property provisions, warranty disclaimers, and limitations of liability.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-primary-950">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-5" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-accent/3 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <FaFileContract className="text-4xl text-accent mx-auto mb-4" />
            <span className="text-accent font-display text-xs tracking-[0.3em]">LEGAL</span>
            <h1 className="text-3xl md:text-5xl font-display font-bold text-white mt-3 mb-3">
              Terms of <span className="text-accent">Service</span>
            </h1>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              Last updated: January 2025
            </p>
            <div className="golden-line mx-auto mt-6" />
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="relative pb-20" ref={ref}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass-card p-6 md:p-8 mb-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <FaHandshake className="text-2xl text-accent" />
              <h2 className="text-lg font-display font-bold text-white">Welcome to Sronzer</h2>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              These Terms of Service govern your use of the Sronzer website and services. 
              By accessing our website, you acknowledge that you have read, understood, and agree 
              to be bound by these Terms. Please read them carefully before using our website.
            </p>
          </motion.div>

          {/* Terms Sections */}
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass-card p-6 md:p-8"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <section.icon className="text-xl text-accent" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-display font-bold text-white">
                    {index + 1}. {section.title}
                  </h2>
                </div>

                <div className="ml-12 space-y-4">
                  {section.content.map((item, idx) => (
                    <div key={idx}>
                      {item.subtitle && (
                        <h3 className="text-accent font-display font-bold text-sm mb-2">
                          {item.subtitle}
                        </h3>
                      )}
                      {item.text && (
                        <p className="text-gray-400 text-sm leading-relaxed">
                          {item.text}
                        </p>
                      )}
                      {item.list && (
                        <ul className="space-y-2 mt-2">
                          {item.list.map((listItem, i) => (
                            <li key={i} className="flex items-start space-x-2 text-gray-400 text-sm">
                              <span className="text-accent mt-1.5">•</span>
                              <span>{listItem}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Export Control Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="glass-card p-6 md:p-8 mt-8 border border-yellow-500/20"
          >
            <div className="flex items-start space-x-3">
              <FaExclamationTriangle className="text-2xl text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-lg font-display font-bold text-yellow-500 mb-3">
                  Export Control Notice
                </h2>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Products and technologies described on this website are subject to Indian export 
                  control laws and regulations, including the Special Chemicals, Organisms, Materials, 
                  Equipment and Technologies (SCOMET) list. Export, re-export, or transfer of these 
                  items may require authorization from the Government of India. By accessing information 
                  about these products, you agree to comply with all applicable export control laws.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="glass-card p-6 md:p-8 mt-8"
          >
            <h2 className="text-xl font-display font-bold text-white mb-4">Questions About These Terms?</h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact our legal team:
            </p>
            <div className="space-y-2 text-sm text-gray-300">
              <p><span className="text-accent">Email:</span> connect@tavasindustries.com</p>
              <p><span className="text-accent">Phone:</span> +91 7440755715</p>
              <p><span className="text-accent">Address:</span> No.303 A(5), Akriti Business Center, ABC Mall, Bhopal, MP 462026</p>
            </div>
          </motion.div>

          {/* Bottom Navigation */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <Link 
              to="/privacy"
              className="text-accent hover:text-accent-light transition-colors font-display text-sm tracking-wider"
            >
              VIEW PRIVACY POLICY →
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;